/* global document window navigator */

const BLUR_CLASS = 'media-protected--blurred';
const KEYBOARD_LOCK_MS = 10000;
const ALERT_COOLDOWN_MS = 4000;
const PROTECTED_MEDIA_SELECTOR = 'img.Photo, video.Video, img.SendPhotoForm__item__photo';

const PRINT_DISABLED_MESSAGES = {
	pt: 'Ação de impressão desativada no site!',
	en: 'Printing is disabled on this site!',
	es: '¡La impresión está desactivada en el sitio!',
};

const SCREENSHOT_DISABLED_MESSAGES = {
	pt: 'Captura de tela desativada no site!',
	en: 'Screenshots are disabled on this site!',
	es: '¡La captura de pantalla está desactivada en el sitio!',
};

let protectionLocked = false;
let protectionUnlockTimer = null;
let lastAlertAt = 0;
let obscured = false;
const protectionListeners = new Set();
const obscuredListeners = new Set();
const hiddenMediaSources = new WeakMap();

export function preventMediaContextMenu(event) {
	event.preventDefault();
}

export function preventMediaDrag(event) {
	event.preventDefault();
}

export function isMediaProtectionActive() {
	return protectionLocked;
}

export function subscribeMediaProtection(listener) {
	protectionListeners.add(listener);
	listener(protectionLocked);

	return () => {
		protectionListeners.delete(listener);
	};
}

function notifyProtectionListeners() {
	protectionListeners.forEach((listener) => {
		listener(protectionLocked);
	});
}

function notifyObscuredListeners() {
	obscuredListeners.forEach((listener) => {
		listener(obscured);
	});
}

export function subscribeObscured(listener) {
	obscuredListeners.add(listener);
	listener(obscured);

	return () => {
		obscuredListeners.delete(listener);
	};
}

function getLocaleMessage(messages) {
	const lang = (navigator.language || 'pt').slice(0, 2);
	return messages[lang] || messages.pt;
}

function getPrintDisabledMessage() {
	return getLocaleMessage(PRINT_DISABLED_MESSAGES);
}

function getScreenshotDisabledMessage() {
	return getLocaleMessage(SCREENSHOT_DISABLED_MESSAGES);
}

function clearProtectionUnlockTimer() {
	if (protectionUnlockTimer) {
		window.clearTimeout(protectionUnlockTimer);
		protectionUnlockTimer = null;
	}
}

function hideProtectedMediaElements() {
	document.querySelectorAll(PROTECTED_MEDIA_SELECTOR).forEach((element) => {
		const currentSource = element.currentSrc || element.src;

		if (!currentSource || hiddenMediaSources.has(element)) {
			return;
		}

		hiddenMediaSources.set(element, currentSource);
		element.removeAttribute('src');
		element.style.visibility = 'hidden';
	});
}

function restoreProtectedMediaElements() {
	document.querySelectorAll(PROTECTED_MEDIA_SELECTOR).forEach((element) => {
		const storedSource = hiddenMediaSources.get(element);

		if (!storedSource) {
			return;
		}

		element.src = storedSource;
		element.style.visibility = '';
		hiddenMediaSources.delete(element);
	});
}

function releasePhotoProtection() {
	protectionLocked = false;
	clearProtectionUnlockTimer();
	document.body.classList.remove(BLUR_CLASS);
	restoreProtectedMediaElements();
	notifyProtectionListeners();
}

function scheduleProtectionUnlock(delay) {
	clearProtectionUnlockTimer();
	protectionUnlockTimer = window.setTimeout(releasePhotoProtection, delay);
}

function forceBlurPaint() {
	void document.body.offsetHeight;
}

function showProtectionAlert(message) {
	const now = Date.now();
	if (now - lastAlertAt < ALERT_COOLDOWN_MS) {
		return;
	}

	lastAlertAt = now;
	window.alert(message);
}

function deferProtectionAlert(message) {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			showProtectionAlert(message);
		});
	});
}

function applyPhotoProtection({ autoUnlockMs = 0, alertMessage = null } = {}) {
	document.body.classList.add(BLUR_CLASS);
	protectionLocked = true;
	hideProtectedMediaElements();
	notifyProtectionListeners();
	forceBlurPaint();

	if (alertMessage) {
		deferProtectionAlert(alertMessage);
	}

	if (autoUnlockMs > 0) {
		scheduleProtectionUnlock(autoUnlockMs);
	}
}

function isScreenshotShortcut(event) {
	if (event.key === 'PrintScreen' || event.code === 'PrintScreen') {
		return true;
	}

	if (event.metaKey && event.shiftKey && ['3', '4', '5'].includes(event.key)) {
		return true;
	}

	return false;
}

function isWindowsSnippingShortcut(event) {
	return event.shiftKey && (event.key === 's' || event.key === 'S' || event.code === 'KeyS')
		&& (event.metaKey || event.getModifierState('Meta') || event.getModifierState('OS'));
}

function isEarlyCaptureKey(event) {
	return isScreenshotShortcut(event) || isWindowsSnippingShortcut(event);
}

function isPrintShortcut(event) {
	return (event.ctrlKey || event.metaKey) && (event.key === 'p' || event.key === 'P');
}

function isProtectedMediaTarget(target) {
	if (!target || !target.closest) {
		return false;
	}

	return Boolean(target.closest('.Photo, .Video, .MediaModal, .SendPhotoForm__item__photo'));
}

function triggerScreenshotProtection() {
	applyPhotoProtection({
		autoUnlockMs: KEYBOARD_LOCK_MS,
		alertMessage: getScreenshotDisabledMessage(),
	});
}

function handleEarlyCaptureKey(event) {
	if (!isEarlyCaptureKey(event)) {
		return;
	}

	triggerScreenshotProtection();
}

function handleScreenshotKeyUp(event) {
	if (event.key === 'PrintScreen' || event.code === 'PrintScreen' || event.keyCode === 44) {
		triggerScreenshotProtection();
	}
}

function handlePrintShortcut(event) {
	if (!isPrintShortcut(event)) {
		return;
	}

	event.preventDefault();
	applyPhotoProtection({
		autoUnlockMs: KEYBOARD_LOCK_MS,
		alertMessage: getPrintDisabledMessage(),
	});
}

function handleBeforePrint() {
	applyPhotoProtection({
		autoUnlockMs: KEYBOARD_LOCK_MS,
		alertMessage: getPrintDisabledMessage(),
	});
}

function handleAfterPrint() {
	scheduleProtectionUnlock(KEYBOARD_LOCK_MS);
}

function handleCopy(event) {
	if (isProtectedMediaTarget(event.target)) {
		event.preventDefault();
	}
}

function handleRestoreInteraction(event) {
	if (!document.body.classList.contains(BLUR_CLASS)) {
		return;
	}

	event.preventDefault();
	event.stopPropagation();
	releasePhotoProtection();
}

export function initMediaFocusBlurProtection() {
	document.addEventListener('keydown', handleEarlyCaptureKey, true);
	document.addEventListener('keyup', handleScreenshotKeyUp, true);
	document.addEventListener('keydown', handlePrintShortcut, true);
	window.addEventListener('beforeprint', handleBeforePrint);
	window.addEventListener('afterprint', handleAfterPrint);
	document.addEventListener('copy', handleCopy);
	document.addEventListener('cut', handleCopy);
	document.addEventListener('mousedown', handleRestoreInteraction, true);
	document.addEventListener('touchstart', handleRestoreInteraction, true);

	return () => {
		document.removeEventListener('keydown', handleEarlyCaptureKey, true);
		document.removeEventListener('keyup', handleScreenshotKeyUp, true);
		document.removeEventListener('keydown', handlePrintShortcut, true);
		window.removeEventListener('beforeprint', handleBeforePrint);
		window.removeEventListener('afterprint', handleAfterPrint);
		document.removeEventListener('copy', handleCopy);
		document.removeEventListener('cut', handleCopy);
		document.removeEventListener('mousedown', handleRestoreInteraction, true);
		document.removeEventListener('touchstart', handleRestoreInteraction, true);
		releasePhotoProtection();
	};
}

export const isPrintScreenKey = (event) => {
	const { code, key, keyCode } = event;

	if (code === 'PrintScreen' || key === 'PrintScreen') return true;
	if (code === 'Print' || key === 'Print') return true;
	if (keyCode === 44) return true;

	return false;
};

export const isMacScreenshotShortcut = (event) => {
	if (!event.metaKey || !event.shiftKey) return false;
	return ['3', '4', '5'].includes(event.key);
};

export const isLinuxScreenshotShortcut = (event) => {
	if (!isPrintScreenKey(event)) return false;
	return event.shiftKey || event.altKey || event.ctrlKey;
};

export const shouldObscureOnKeyDown = (event) => {
	if (isPrintScreenKey(event)) return true;
	if (isMacScreenshotShortcut(event)) return true;
	return false;
};

export const shouldBlockShortcut = (event) => {
	const { ctrlKey, metaKey, key, code } = event;
	const mod = ctrlKey || metaKey;

	if (key === 'F12' || code === 'F12') return true;
	if (mod && (key === 's' || key === 'S')) return true;
	if (mod && (key === 'p' || key === 'P')) return true;
	if (mod && event.shiftKey && (key === 'I' || key === 'i')) return true;
	if (mod && event.shiftKey && (key === 'J' || key === 'j')) return true;

	return shouldObscureOnKeyDown(event);
};

export const obscure = () => {
	if (typeof document === 'undefined' || !document.body) return;
	if (obscured) return;

	obscured = true;
	notifyObscuredListeners();
	forceBlurPaint();
};

export const restore = () => {
	if (typeof document === 'undefined' || !document.body) return;
	if (!obscured) return;

	obscured = false;
	notifyObscuredListeners();
};

export const isObscured = () => {
	if (typeof document === 'undefined' || !document.body) return false;
	return obscured;
};
